export const assetKind = (asset) => ({ CAM: "Câmera", SER: "Servidor", SWI: "Switch", NOB: "Nobreak", RAC: "Rack" })[asset.systemType] || asset.assetType || "Equipamento";
export const assetLabel = (asset) => asset.pointName || asset.name;
export const assetLocation = (asset) => asset.building || asset.area || asset.location || "Local não informado";

// Only persisted switch/UPS relations are physical links. Location branches merely
// organize the drawing and are rendered with dashed lines.
export function buildTopology(assets, unitName) {
  const byId = new Map(assets.map((asset) => [Number(asset.id), asset]));
  const hasPhysicalLink = (asset) => [asset.switchAssetId, asset.upsAssetId].some((id) => byId.has(Number(id)) && Number(id) !== Number(asset.id));
  // An identified point belongs on the map even when its physical switch is
  // unknown. Its dashed location edge must not be presented as a cable link.
  const displayed = assets;
  const unlinked = assets.filter((asset) => !hasPhysicalLink(asset) && !assets.some((other) => Number(other.switchAssetId) === Number(asset.id) || Number(other.upsAssetId) === Number(asset.id)));
  const locations = [...new Set(displayed.map(assetLocation))].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const nodes = [{ id: "unit", type: "unit", label: unitName || "Unidade" }];
  const edges = [];
  const keyForLocation = (name) => `location:${locations.indexOf(name)}`;
  locations.forEach((name) => {
    nodes.push({ id: keyForLocation(name), type: "location", label: name });
    edges.push({ source: "unit", target: keyForLocation(name), type: "group" });
  });
  displayed.forEach((asset) => nodes.push({ id: `asset:${asset.id}`, type: "asset", label: assetLabel(asset), asset }));
  const displayedIds = new Set(displayed.map((asset) => Number(asset.id)));
  const parentOf = new Map();
  displayed.forEach((asset) => {
    const id = Number(asset.id);
    const preferred = [asset.switchAssetId, asset.upsAssetId].map(Number).find((target) => displayedIds.has(target) && target !== id);
    let parent = preferred;
    const seen = new Set([id]);
    while (parent && !seen.has(parent)) {
      seen.add(parent);
      const next = byId.get(parent);
      parent = Number(next?.switchAssetId) || Number(next?.upsAssetId) || 0;
    }
    if (parent && seen.has(parent)) parentOf.set(id, null); // Ignore a cyclic relationship.
    else parentOf.set(id, preferred || null);
  });
  displayed.forEach((asset) => {
    const parent = parentOf.get(Number(asset.id));
    edges.push({ source: parent ? `asset:${parent}` : keyForLocation(assetLocation(asset)), target: `asset:${asset.id}`, type: parent ? "physical" : "group", port: parent === Number(asset.switchAssetId) ? asset.switchPort : "" });
    if (asset.upsAssetId && Number(asset.upsAssetId) !== parent && displayedIds.has(Number(asset.upsAssetId))) {
      edges.push({ source: `asset:${asset.upsAssetId}`, target: `asset:${asset.id}`, type: "power" });
    }
  });
  const children = new Map(nodes.map((node) => [node.id, []]));
  edges.filter((edge) => edge.type !== "power").forEach((edge) => children.get(edge.source)?.push(edge.target));
  for (const siblings of children.values()) siblings.sort((a, b) => {
    const left = nodes.find((node) => node.id === a);
    const right = nodes.find((node) => node.id === b);
    const rank = (node) => ({ location: 0, NOB: 1, RAC: 2, SWI: 3, SER: 4, CAM: 5 })[node.asset?.systemType || node.type] ?? 6;
    return rank(left) - rank(right) || left.label.localeCompare(right.label, "pt-BR");
  });
  const positions = new Map();
  let leaf = 0;
  const visit = (id, depth) => {
    const descendants = children.get(id) || [];
    descendants.forEach((child) => visit(child, depth + 1));
    const y = descendants.length ? (positions.get(descendants[0]).y + positions.get(descendants[descendants.length - 1]).y) / 2 : 88 + leaf++ * 88;
    positions.set(id, { x: 106 + depth * 228, y });
  };
  visit("unit", 0);
  const laidOut = nodes.map((node) => ({ ...node, ...positions.get(node.id) }));
  return { nodes: laidOut, edges, unlinked, width: Math.max(550, ...laidOut.map((node) => node.x + 155)), height: Math.max(350, ...laidOut.map((node) => node.y + 88)) };
}
